import type { KkmHandshakeReady, KkmServerAdapter } from '@/shared/api';

import { selectAcquiringTerminal, parseMarkingValidation } from '@/entities/fiscalization';
import {
  type OrderFiscalExecutionFieldsFragment,
  OrderFiscalKktStatus,
  OrderFiscalTerminalStatus,
  type PrepareOrderFiscalSettlementInput,
  type RetryOrderFiscalReceiptInput,
} from '@shared/api/graphql/gql/graphql';

import { executeKkmSafely } from './execute-kkm-safely';

/** `kkt` обязателен для повтора печати, `terminal` — как в prepare. */
type OrderFiscalEquipmentVerificationInput = Pick<RetryOrderFiscalReceiptInput, 'kkt'> &
  Pick<PrepareOrderFiscalSettlementInput, 'terminal'>;

export function orderFiscalEquipmentVerification(handshake: KkmHandshakeReady): OrderFiscalEquipmentVerificationInput {
  const terminal = selectAcquiringTerminal(handshake);

  return {
    kkt: {
      deviceId: handshake.kktDevice.idDevice,
      // Факты регистрации ККТ: бэкенд по ним решает, можно ли пробить чек с
      // кодом маркировки или подакцизной строкой.
      ffdVersion: handshake.kkt.ffdVersion,
      fnNumber: handshake.kkt.fnNumber,
      inn: handshake.kkt.inn,
      kktNumber: handshake.kkt.kktNumber,
      model: handshake.kkt.model ?? handshake.kktDevice.name,
      saleExcisableGoods: handshake.kkt.saleExcisableGoods,
      saleMarking: handshake.kkt.saleMarking,
      status: OrderFiscalKktStatus.Ready,
    },
    terminal:
      terminal === undefined
        ? undefined
        : {
            deviceId: terminal.device.idDevice,
            name: terminal.device.name,
            number: terminal.device.number,
            paymentKind: terminal.status === 'ready' ? 'card' : null,
            status:
              terminal.status === 'ready' ? OrderFiscalTerminalStatus.Ready : OrderFiscalTerminalStatus.Unavailable,
          },
  };
}

/**
 * Коды маркировки проверяются до списания с карты: отказ ККТ после оплаты оставил
 * бы деньги без чека. Неясный ответ проверки оплату не останавливает: чек всё
 * равно не напечатается с непрошедшим кодом.
 */
export async function validateReceiptMarking(
  adapter: Pick<KkmServerAdapter, 'validateMarkingCodes'>,
  execution: OrderFiscalExecutionFieldsFragment,
): Promise<string | null> {
  const codes = execution.receipt_plan.items
    .map((item) => item.marking_code)
    .filter((code): code is string => typeof code === 'string' && code !== '');

  if (codes.length === 0) return null;

  const result = await executeKkmSafely(() =>
    adapter.validateMarkingCodes(execution.execution_target.kkt_device_id, codes),
  );
  const failed = parseMarkingValidation(result.response.raw).find(
    (validation) => validation.prPassed === false || validation.kktPassed === false,
  );
  const code = failed?.decryptionResult ?? failed?.barCode ?? null;

  return failed === undefined
    ? null
    : `Касса отклонила код маркировки${code === null ? '' : ` …${code.slice(-6)}`}. Уберите позицию и пробейте чек заново.`;
}
